import { supabase } from "@/lib/supabase";

/**
 * VIB-757: Optimistic locking conflict — caster når `updated_at`-felt
 * ikke matcher forventet verdi ved update. Caller har stale data og må
 * laste på nytt før de prøver igjen.
 */
export class OptimisticLockConflictError extends Error {
  constructor(
    public readonly table: string,
    public readonly id: string,
  ) {
    super(`Optimistic lock conflict on ${table}#${id}`);
    this.name = "OptimisticLockConflictError";
  }
}

/**
 * Tabeller som har `updated_at` med automatisk trigger og som vi har
 * bestemt skal håndheve optimistic locking. Holdes smal slik at vi
 * ikke aktiverer locking på tabeller uten trigger.
 */
/**
 * Tabeller der to personer kan redigere samme rad samtidig.
 *
 * `documents` kom til med VIB-2722: så snart en eier kan slippe inn
 * medredaktører, kan to personer ha samme dokument oppe, og den som lagrer
 * sist ville ellers overskrevet den første i stillhet.
 */
export type LockableTable = "projects" | "documents";

/**
 * Update en rad med optimistic lock. Sjekker at `updated_at` matcher
 * `expectedUpdatedAt` i WHERE-klausulen. Hvis 0 rader påvirkes betyr
 * det at noen andre har oppdatert raden mellom lese og skrive — caster
 * OptimisticLockConflictError som caller skal håndtere i `onError`.
 */
export async function updateWithOptimisticLock<T extends Record<string, unknown>>(
  table: LockableTable,
  id: string,
  patch: T,
  expectedUpdatedAt: string,
): Promise<void> {
  const { data, error } = await supabase
    .from(table)
    .update(patch as never)
    .eq("id", id)
    .eq("updated_at", expectedUpdatedAt)
    .select("id");

  if (error) throw new Error(error.message);
  if (!data || data.length === 0) {
    throw new OptimisticLockConflictError(table, id);
  }
}
